import React, { useEffect, useRef } from 'react'
import { LottieChat } from '@/components/chat-lottie'
import { useEnginePlayActions } from '../hooks/use-engine-play-action'
import { useGalgameController } from '../hooks/use-galgame-controller'
import { useEngineStore } from '../stores/use-engine-store'
import { GalgameTemplateRender } from './galgame-render'

interface GalgameContainerProps {
  sessionId: string
}

export default function GalgameContainer({ sessionId }: GalgameContainerProps) {
  const { messages: messagesSlice } = useEngineStore()
  const { isPolling, isMessagesLoading } = useEnginePlayActions(sessionId)

  const messages = messagesSlice.messages
  const {
    initSegments,
    nextStep,
    galgameSegments,
    currentMessageIndex,
    resetGalgameState,
  } = useGalgameController()

  // 用于追踪上一个 sessionId，检测会话切换
  const prevSessionIdRef = useRef<string | null>(null)
  // 用于标记是否刚刚执行了重置（需要等待新消息加载后再初始化）
  const needsReinitRef = useRef<boolean>(false)

  // 会话切换时重置 Galgame 状态
  useEffect(() => {
    // 首次加载时记录 sessionId
    if (prevSessionIdRef.current === null) {
      prevSessionIdRef.current = sessionId
      return
    }

    // 检测到会话切换
    if (prevSessionIdRef.current !== sessionId) {
      prevSessionIdRef.current = sessionId
      // 标记需要重新初始化
      needsReinitRef.current = true
      // 重置状态，让下一个 useEffect 重新初始化
      resetGalgameState()
    }
  }, [sessionId, resetGalgameState])

  // 初始化段落逻辑：
  // 1. 只有当没有段落数据时才进行初始化（初次加载 or 会话切换后重置）
  // 2. 当消息总数增加且用户原本就在看最新消息时，自动追随到新消息
  // 3. 会话切换后，等新消息加载完成再初始化
  useEffect(() => {
    if (!messages || messages.length === 0) return

    // 如果标记了需要重新初始化（会话切换后），强制初始化
    if (needsReinitRef.current) {
      needsReinitRef.current = false
      initSegments()
      return
    }

    const isInitialLoad = galgameSegments.length === 0
    const isNewMessageArrived =
      messages.length > 0 && currentMessageIndex < messages.length - 1

    if (isInitialLoad) {
      initSegments()
    } else if (
      isNewMessageArrived &&
      currentMessageIndex === messages.length - 2
    ) {
      // 如果是在游玩过程中产生了新回复（比如发送了消息后得到回复），自动跳到最新
      initSegments()
    }
  }, [
    messages?.length,
    galgameSegments.length,
    initSegments,
    currentMessageIndex,
  ])

  // 处理全屏点击以推进对话
  const handleScreenClick = (e: React.MouseEvent) => {
    // 如果点击的是按钮或其他交互元素，则不触发推进
    if ((e.target as HTMLElement).closest('button')) return

    // 清除任何已存在的文字选中，防止意外选中文字
    window.getSelection()?.removeAllRanges()

    nextStep()
  }

  if (isMessagesLoading) {
    return (
      <div className='flex h-full w-full items-center justify-center'>
        <LottieChat className='h-24 w-24' loop />
      </div>
    )
  }

  const currentMessage = messages && messages[currentMessageIndex]
  if (!currentMessage) return null

  return (
    <div
      onClick={handleScreenClick}
      className='relative h-full w-full overflow-hidden select-none'
    >
      {/* 立绘层 (Placeholder) */}
      <div className='absolute inset-x-0 top-0 bottom-0 flex items-center justify-center p-8'>
        {/* 可以在这里根据当前角色展示立绘 */}
        <div className='pointer-events-none absolute inset-0 bg-linear-to-t from-black/50 to-transparent' />
      </div>

      {/* 轮询等待动画层 - 在对话框上方居中显示 */}
      {isPolling && (
        <div className='animate-in fade-in-0 absolute inset-x-0 bottom-1/3 z-20 flex justify-center duration-300'>
          <div className='rounded-full bg-black/30 p-3 backdrop-blur-sm'>
            <LottieChat
              className='h-20 w-20 drop-shadow-[0_0_10px_rgba(255,255,255,0.6)]'
              loop
            />
          </div>
        </div>
      )}

      {/* 渲染层（包含对话框和选项） */}
      <div className='absolute inset-0 z-10 flex flex-col justify-end'>
        <GalgameTemplateRender
          message={currentMessage}
          sessionId={sessionId}
          context={undefined}
        />
      </div>
    </div>
  )
}
